/**
 * Tipos para Segmentação Avançada de Campanhas
 * 
 * Define a estrutura de segmentos, grupos de filtros e filtros individuais
 * que permitem criar audiências dinâmicas baseadas em dados internos do sistema.
 * 
 * @author Sistema de Campanhas
 * @version 1.0.0
 */

// =============================================================================
// TIPOS DE FILTROS
// =============================================================================

/**
 * Tipos de filtros disponíveis para segmentação
 * Nota: Filtros de CRM só são exibidos se o tenant tiver crm_enabled = true
 */
export type SegmentFilterType =
  // Filtros de Contato (sempre disponíveis)
  | 'contact_tag'           // Tags de contato
  | 'contact_seller'        // Vendedor do contato
  
  // Filtros de Conversa (sempre disponíveis)
  | 'conversation_status'   // Status da conversa (open, pending, closed, bot)
  | 'conversation_inbox'    // Inbox da conversa
  | 'conversation_department' // Departamento
  | 'conversation_agent'    // Agente responsável
  | 'conversation_phase'    // Fase do Kanban de conversas
  
  // Filtros de CRM/Negócio (apenas se crm_enabled)
  | 'deal_status'           // Status do negócio (open, won, lost, etc.)
  | 'deal_tag'              // Tags de negócio
  | 'deal_phase'            // Fase do pipeline
  | 'deal_workspace'        // Workspace do CRM
  | 'deal_source'           // Origem do lead
  | 'deal_seller';          // Vendedor do deal

/**
 * Categorias de filtros para agrupamento na UI
 */
export type SegmentFilterCategory = 'contact' | 'conversation' | 'deal';

/**
 * Mapa de filtro para categoria
 */
export const FILTER_CATEGORIES: Record<SegmentFilterType, SegmentFilterCategory> = {
  // Contato
  contact_tag: 'contact',
  contact_seller: 'contact',
  
  // Conversa
  conversation_status: 'conversation',
  conversation_inbox: 'conversation',
  conversation_department: 'conversation',
  conversation_agent: 'conversation',
  conversation_phase: 'conversation',
  
  // CRM/Negócio
  deal_status: 'deal',
  deal_tag: 'deal',
  deal_phase: 'deal',
  deal_workspace: 'deal',
  deal_source: 'deal',
  deal_seller: 'deal',
};

/**
 * Filtros que pertencem ao módulo CRM (exibidos apenas se crm_enabled)
 */
export const CRM_FILTER_TYPES: SegmentFilterType[] = [
  'deal_status',
  'deal_tag',
  'deal_phase',
  'deal_workspace',
  'deal_source',
  'deal_seller',
];

/**
 * Configuração de cada tipo de filtro para a UI
 */
export interface FilterTypeConfig {
  type: SegmentFilterType;
  label: string;
  category: SegmentFilterCategory;
  valueType: 'single' | 'multi' | 'status';
  placeholder: string;
  isCrmOnly?: boolean;
}

/**
 * Configurações de todos os tipos de filtros
 */
export const FILTER_TYPE_CONFIGS: FilterTypeConfig[] = [
  // Contato
  { type: 'contact_tag', label: 'Tag de Contato', category: 'contact', valueType: 'multi', placeholder: 'Selecione tags...' },
  { type: 'contact_seller', label: 'Vendedor do Contato', category: 'contact', valueType: 'single', placeholder: 'Selecione vendedor...' },
  
  // Conversa
  { type: 'conversation_status', label: 'Status da Conversa', category: 'conversation', valueType: 'multi', placeholder: 'Selecione status...' },
  { type: 'conversation_inbox', label: 'Inbox', category: 'conversation', valueType: 'multi', placeholder: 'Selecione inboxes...' },
  { type: 'conversation_department', label: 'Departamento', category: 'conversation', valueType: 'multi', placeholder: 'Selecione departamentos...' },
  { type: 'conversation_agent', label: 'Agente Responsável', category: 'conversation', valueType: 'single', placeholder: 'Selecione agente...' },
  { type: 'conversation_phase', label: 'Fase do Kanban', category: 'conversation', valueType: 'multi', placeholder: 'Selecione fases...' },
  
  // CRM/Negócio
  { type: 'deal_status', label: 'Status do Negócio', category: 'deal', valueType: 'multi', placeholder: 'Selecione status...', isCrmOnly: true },
  { type: 'deal_tag', label: 'Tag de Negócio', category: 'deal', valueType: 'multi', placeholder: 'Selecione tags...', isCrmOnly: true },
  { type: 'deal_phase', label: 'Fase do Pipeline', category: 'deal', valueType: 'multi', placeholder: 'Selecione fases...', isCrmOnly: true },
  { type: 'deal_workspace', label: 'Workspace', category: 'deal', valueType: 'single', placeholder: 'Selecione workspace...', isCrmOnly: true },
  { type: 'deal_source', label: 'Origem do Lead', category: 'deal', valueType: 'multi', placeholder: 'Selecione origens...', isCrmOnly: true },
  { type: 'deal_seller', label: 'Vendedor do Negócio', category: 'deal', valueType: 'single', placeholder: 'Selecione vendedor...', isCrmOnly: true },
];

// =============================================================================
// OPERADORES
// =============================================================================

/**
 * Operadores de comparação disponíveis
 */
export type SegmentOperator = 
  | 'is_any'       // É qualquer um de (para multi-select)
  | 'is_not_any'   // Não é nenhum de (para multi-select)
  | 'equals'       // Igual a (para single-select)
  | 'not_equals';  // Diferente de (para single-select)

/**
 * Labels dos operadores para UI
 */
export const OPERATOR_LABELS: Record<SegmentOperator, string> = {
  is_any: 'é qualquer um de',
  is_not_any: 'não é nenhum de',
  equals: 'é igual a',
  not_equals: 'é diferente de',
};

/**
 * Operadores válidos por tipo de valor
 */
export const OPERATORS_BY_VALUE_TYPE: Record<'single' | 'multi' | 'status', SegmentOperator[]> = {
  single: ['equals', 'not_equals'],
  multi: ['is_any', 'is_not_any'],
  status: ['is_any', 'is_not_any'],
};

// =============================================================================
// ESTRUTURAS DE DADOS
// =============================================================================

/**
 * Opção genérica para selects
 */
export interface FilterOption {
  value: string;
  label: string;
  color?: string;  // Para tags e fases coloridas
  icon?: string;   // Nome do ícone (opcional)
}

/**
 * Filtro individual dentro de um grupo
 */
export interface SegmentFilter {
  id: string;
  type: SegmentFilterType;
  operator: SegmentOperator;
  value: string[];  // Sempre array, mesmo para single-select (facilita a lógica)
}

/**
 * Grupo de filtros (unidos por AND)
 */
export interface SegmentFilterGroup {
  id: string;
  filters: SegmentFilter[];
}

/**
 * Segmento completo (grupos unidos por OR)
 */
export interface CampaignSegment {
  groups: SegmentFilterGroup[];
}

// =============================================================================
// RESULTADO DO PREVIEW
// =============================================================================

/**
 * Contato retornado pelo preview do segmento
 */
export interface SegmentPreviewContact {
  id: string;
  name: string | null;
  phone: string;
  email?: string | null;
  match_reason?: string;  // Qual grupo/filtro deu match
}

/**
 * Resultado completo do preview
 */
export interface SegmentPreviewResult {
  contacts: SegmentPreviewContact[];
  total_count: number;
  is_truncated: boolean;  // true se total_count > limite do preview
}

// =============================================================================
// STATUS DE CONVERSA E NEGÓCIO
// =============================================================================

/**
 * Status disponíveis para conversas
 */
export const CONVERSATION_STATUSES: FilterOption[] = [
  { value: 'open', label: 'Aberta' },
  { value: 'pending', label: 'Pendente' },
  { value: 'closed', label: 'Fechada' },
  { value: 'bot', label: 'Bot' },
];

/**
 * Status disponíveis para negócios
 */
export const DEAL_STATUSES: FilterOption[] = [
  { value: 'open', label: 'Aberto' },
  { value: 'won', label: 'Ganho' },
  { value: 'lost', label: 'Perdido' },
  { value: 'archived', label: 'Arquivado' },
];

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Cria um filtro vazio
 */
export function createEmptyFilter(): SegmentFilter {
  return {
    id: crypto.randomUUID(),
    type: 'contact_tag',
    operator: 'is_any',
    value: [],
  };
}

/**
 * Cria um grupo vazio com um filtro
 */
export function createEmptyGroup(): SegmentFilterGroup {
  return {
    id: crypto.randomUUID(),
    filters: [createEmptyFilter()],
  };
}

/**
 * Cria um segmento vazio com um grupo
 */
export function createEmptySegment(): CampaignSegment {
  return {
    groups: [createEmptyGroup()],
  };
}

/**
 * Verifica se um segmento é válido (tem pelo menos um filtro com valor)
 */
export function isSegmentValid(segment: CampaignSegment): boolean {
  return segment.groups.some(group => 
    group.filters.some(filter => filter.value.length > 0)
  );
}

/**
 * Conta o total de filtros ativos em um segmento
 */
export function countActiveFilters(segment: CampaignSegment): number {
  return segment.groups.reduce((count, group) => 
    count + group.filters.filter(f => f.value.length > 0).length
  , 0);
}

/**
 * Obtém os tipos de filtros disponíveis baseado em crm_enabled
 */
export function getAvailableFilterTypes(crmEnabled: boolean): FilterTypeConfig[] {
  if (crmEnabled) {
    return FILTER_TYPE_CONFIGS;
  }
  return FILTER_TYPE_CONFIGS.filter(config => !config.isCrmOnly);
}
